import { BOARD_SIZE, boardFromMoves, winningLine } from './rules';
import type { Difficulty, Move, PlayerSide, Rule } from './types';

export interface RapfiCandidate {
  move: [number, number];
  rank: number;
  evaluation?: number;
  winRate?: number;
  stdev?: number;
  lcbValue?: number;
}

export interface MoveInsight {
  label: string;
  message: string;
}

const DIRECTIONS: Array<[number, number]> = [[1, 0], [0, 1], [1, 1], [1, -1]];

function tacticalScore(moves: Move[], position: [number, number], side: PlayerSide): number {
  const board = boardFromMoves(moves);
  const [x, y] = position;
  if (board[y]?.[x]) return -Infinity;
  board[y][x] = side;
  let score = 0;
  for (const [dx, dy] of DIRECTIONS) {
    let length = 1;
    let openEnds = 0;
    for (const direction of [-1, 1]) {
      let step = 1;
      while (board[y + dy * step * direction]?.[x + dx * step * direction] === side) {
        length += 1;
        step += 1;
      }
      if (board[y + dy * step * direction]?.[x + dx * step * direction] === 0) openEnds += 1;
    }
    const base = [0, 1, 8, 34, 150, 1000][Math.min(length, 5)];
    score += base * (openEnds === 2 ? 1.45 : openEnds === 1 ? 1 : 0.45);
  }
  return score;
}

function immediateWinningCells(moves: Move[], side: PlayerSide, rule: Rule): Set<string> {
  const board = boardFromMoves(moves);
  const wins = new Set<string>();
  for (let y = 0; y < BOARD_SIZE; y += 1) {
    for (let x = 0; x < BOARD_SIZE; x += 1) {
      if (board[y][x]) continue;
      const move: Move = { x, y, side };
      const nextMoves = [...moves, move];
      if (winningLine(boardFromMoves(nextMoves), move, rule)) wins.add(`${x},${y}`);
    }
  }
  return wins;
}

export function explainStyledMove(
  moves: Move[],
  rule: Rule,
  difficulty: Difficulty,
  move: [number, number],
): MoveInsight {
  const side = (moves.length % 2 === 0 ? 1 : 2) as PlayerSide;
  const opponent = (side === 1 ? 2 : 1) as PlayerSide;
  const key = `${move[0]},${move[1]}`;
  if (immediateWinningCells(moves, side, rule).has(key)) {
    return { label: '勝負手', message: 'Rapfi 看見了直接完成五連的落點。' };
  }
  if (immediateWinningCells(moves, opponent, rule).has(key)) {
    return { label: '防守手', message: 'Rapfi 先封住你下一手可以完成的五連。' };
  }
  const messages: Record<Difficulty['styleType'], string> = {
    growth: '從 Rapfi 的優先候選中，選擇較自然、保留變化的延伸。',
    defense: '在 Rapfi 候選中偏向壓縮你的發展空間。',
    attack: '在 Rapfi 候選中偏向擴張自己的連線與先手。',
    adaptive: '在評價相近的候選中轉換攻守方向，保留突然變奏。',
    balanced: '依 Rapfi 排名，在攻勢與守勢之間取得平衡。',
  };
  return { label: difficulty.styleLabel, message: messages[difficulty.styleType] };
}

export function chooseStyledMove(
  moves: Move[],
  rule: Rule,
  difficulty: Difficulty,
  candidates: RapfiCandidate[],
  fallback: [number, number],
): [number, number] {
  const board = boardFromMoves(moves);
  const legal = candidates
    .filter((candidate) => board[candidate.move[1]]?.[candidate.move[0]] === 0)
    .sort((a, b) => a.rank - b.rank);

  const side = (moves.length % 2 === 0 ? 1 : 2) as PlayerSide;
  const opponent = (side === 1 ? 2 : 1) as PlayerSide;
  const ownWins = immediateWinningCells(moves, side, rule);
  const winning = legal.find(({ move }) => ownWins.has(`${move[0]},${move[1]}`));
  if (winning) return winning.move;

  const opponentWins = immediateWinningCells(moves, opponent, rule);
  if (opponentWins.size) {
    const blocker = legal.find(({ move }) => opponentWins.has(`${move[0]},${move[1]}`));
    if (blocker) return blocker.move;
  }

  if (difficulty.styleType === 'growth') {
    if (!moves.length && board[7][7] === 0) return [7, 7];
    if (moves.length <= 3) {
      const anchor = moves.at(-1)!;
      const distance = ([x, y]: [number, number]) => Math.abs(x - anchor.x) + Math.abs(y - anchor.y);
      const naturalCandidates = legal.filter(({ move }) => distance(move) >= 1 && distance(move) <= 2).slice(0, 3);
      if (naturalCandidates.length) {
        const choice = Math.floor(Math.random() * naturalCandidates.length);
        return naturalCandidates[choice].move;
      }
      const offsets: Array<[number, number]> = [[2, 0], [1, 1], [0, 2], [-1, 1], [-2, 0], [-1, -1], [0, -2], [1, -1], [1, 0], [0, 1], [-1, 0], [0, -1]];
      const nearby = offsets
        .map(([dx, dy]) => [anchor.x + dx, anchor.y + dy] as [number, number])
        .filter(([x, y]) => x >= 0 && x < BOARD_SIZE && y >= 0 && y < BOARD_SIZE && board[y][x] === 0);
      if (nearby.length) return nearby[0];
    }
  }

  if (!legal.length) return fallback;

  if (difficulty.styleType === 'balanced') return legal[0].move;

  const growth = Math.min(1, moves.length / 36);
  const scored = legal.map((candidate) => {
    const attack = tacticalScore(moves, candidate.move, side);
    const defense = tacticalScore(moves, candidate.move, opponent);
    const rankScore = Math.max(0, 5 - candidate.rank) * 18;
    let score = rankScore;
    if (difficulty.styleType === 'attack') score += attack * 1.15 + defense * 0.28;
    if (difficulty.styleType === 'defense') score += attack * 0.32 + defense * 1.25;
    if (difficulty.styleType === 'adaptive') {
      const attackPhase = Math.floor(moves.length / 4) % 2 === 0;
      score += attackPhase ? attack * 1.02 + defense * 0.48 : attack * 0.48 + defense * 1.02;
      score += Math.min(36, Math.max(0, candidate.stdev ?? 0) * 0.16);
    }
    if (difficulty.styleType === 'growth') {
      score = rankScore * (0.6 + growth * 1.15) + attack * (0.36 + growth * 0.2) + defense * (0.4 + growth * 0.2);
    }
    return { candidate, score };
  });
  scored.sort((a, b) => b.score - a.score || a.candidate.rank - b.candidate.rank);
  return scored[0].candidate.move;
}
